<template>
  <AppModal
    :show="isVisible"
    :title="$t('taskWorkspace.statusTransition.title')"
    size="large"
    :gradient-header="false"
    :loading="isLoading"
    @close="handleCancel"
  >
    <!-- Main Content -->
    <div class="task-status-transition-modal">
      <!-- Status Timeline Section -->
      <div class="task-status-transition-modal__timeline">
        <div class="timeline-header">
          <h4 class="timeline-section-title">
            <svg viewBox="0 0 24 24" width="20" height="20" class="timeline-section-icon">
              <path fill="currentColor" d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12.5,7V13L16,15.5L15,17L11,14V7H12.5Z"/>
            </svg>
            {{ $t('taskWorkspace.statusWorkflow') }}
          </h4>
          
          <!-- Current Status Display -->
          <div class="timeline-current-status">
            <span class="current-status-label">
              {{ $t('taskWorkspace.statusTransition.currentStatus') }}:
            </span>
            <div class="status-badge" :class="`status-badge--${currentStatus?.toLowerCase()}`">
              {{ $t(`taskWorkspace.statusOptions.${currentStatus}`) }}
            </div>
          </div>
        </div>
        
        <!-- Loading Timeline -->
        <div v-if="loadingTimeline" class="timeline-loading">
          <span>{{ $t('common.loading') }}...</span>
        </div>
        
        <!-- Timeline Display -->
        <div v-else class="integrated-timeline">
          <div
            v-for="(step, index) in timelineSteps"
            :key="`step-${index}`"
            class="timeline-step"
            :class="{
              current: step.isCurrent,
              completed: step.isCompleted
            }"
          >
            <div class="timeline-step-icon">
              <component :is="getTimelineStepIcon(step.status, step)" />
            </div>
            <div class="timeline-step-content">
              <div class="timeline-step-label">{{ $t(`taskWorkspace.statusOptions.${step.status}`) }}</div>
              <div v-if="step.date" class="timeline-step-date">{{ step.date }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Available Transitions -->
      <div v-if="possibleStatuses.length > 0" class="task-status-transition-modal__options">
        <label class="task-status-transition-modal__label">
          {{ $t('taskWorkspace.statusTransition.selectNewStatus') }}
        </label>
        
        <div class="status-options-grid">
          <button
            v-for="status in possibleStatuses"
            :key="status"
            type="button"
            class="status-option"
            :class="{ 'status-option--selected': selectedStatus === status }"
            @click="selectedStatus = status"
          >
            <div class="status-option__icon" :class="`status-option__icon--${status.toLowerCase()}`">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <circle v-if="status === 'PENDING'" fill="currentColor" cx="12" cy="12" r="8" />
                <path v-else-if="status === 'IN_PROGRESS'" fill="currentColor" d="M8,5.14V19.14L19,12.14L8,5.14Z"/>
                <path v-else-if="status === 'ON_HOLD'" fill="currentColor" d="M14,19H18V5H14M6,19H10V5H6V19Z"/>
                <path v-else-if="status === 'COMPLETED'" fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                <path v-else-if="status === 'CANCELLED'" fill="currentColor" d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
                <path v-else fill="currentColor" d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
              </svg>
            </div>
            <div class="status-option__content">
              <div class="status-option__title">
                {{ getActionLabel(status) }}
              </div>
            </div>
            <div v-if="selectedStatus === status" class="status-option__check">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
              </svg>
            </div>
          </button>
        </div>
      </div>

      <!-- Comment Field -->
      <div v-if="selectedStatus" class="task-status-transition-modal__comment">
        <label class="task-status-transition-modal__label" for="status-comment">
          {{ $t('taskWorkspace.statusTransition.comment') }}
        </label>
        <textarea
          id="status-comment"
          v-model="comment"
          class="comment-textarea"
          :placeholder="$t('taskWorkspace.statusTransition.commentPlaceholder')"
          rows="3"
        ></textarea>
      </div>
    </div>

    <!-- Footer Actions -->
    <template #footer>
      <div class="task-status-transition-modal__actions">
        <AppButton
          type="button"
          variant="outline"
          @click="handleCancel"
          :disabled="isLoading"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          type="button"
          variant="primary"
          @click="handleConfirm"
          :disabled="!selectedStatus || isLoading"
          :loading="isLoading"
        >
          {{ $t('common.confirm') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, watch, computed, h, type VNode } from 'vue'
import { useI18n } from 'vue-i18n'
import AppModal from '@/components/base/Modal.vue'
import AppButton from '@/components/base/AppButton.vue'
import projectTasksService from '@/services/projectTasks.service'
import taskStateMachineService from '@/services/taskStateMachine.service'
import { useNotifications } from '@/composables/useNotifications'
import type { TaskStatus, TaskStatusHistoryDto } from '@/types/task'

// Props
interface Props {
  isVisible: boolean
  projectId: number
  taskId: number
  currentStatus: TaskStatus
}

const props = defineProps<Props>()

// Emits
interface Emits {
  confirm: [status: string, comment?: string]
  cancel: []
  close: []
  success: []
}

const emit = defineEmits<Emits>()

// Composables
const { t } = useI18n()
const notifications = useNotifications()

// Timeline interface
interface TimelineStep {
  status: string
  date: string | null
  isCurrent: boolean
  isCompleted: boolean
}

// State
const selectedStatus = ref<string | null>(null)
const comment = ref<string>('')
const possibleStatuses = ref<string[]>([])
const isLoading = ref(false)
const loadingTimeline = ref(false)
const statusTimeline = ref<TaskStatusHistoryDto[]>([])

// Computed timeline steps
const timelineSteps = computed<TimelineStep[]>(() => {
  if (!statusTimeline.value || statusTimeline.value.length === 0) {
    // If no timeline, show current status only
    return [{
      status: props.currentStatus,
      date: null,
      isCurrent: true,
      isCompleted: false
    }]
  }
  
  const steps: TimelineStep[] = []
  
  // Add the initial status (fromStatus of the first transition)
  if (statusTimeline.value[0].fromStatus) {
    steps.push({
      status: statusTimeline.value[0].fromStatus,
      date: null, // Initial status doesn't have a date
      isCurrent: false,
      isCompleted: true
    })
  }
  
  // Add all transitions
  statusTimeline.value.forEach((entry: TaskStatusHistoryDto, index: number) => {
    const isLast = index === statusTimeline.value.length - 1
    steps.push({
      status: entry.toStatus,
      date: entry.formattedChangedAt || null,
      isCurrent: isLast && entry.toStatus === props.currentStatus,
      isCompleted: !isLast || entry.toStatus !== props.currentStatus
    })
  })
  
  return steps
})

// Get context-aware action label
const getActionLabel = (status: string): string => {
  // Context-aware action labels for specific transitions
  if (status === 'IN_PROGRESS') {
    if (props.currentStatus === 'PENDING') {
      return t('taskWorkspace.statusActions.START')
    } else if (props.currentStatus === 'ON_HOLD') {
      return t('taskWorkspace.statusActions.RESUME')
    }
  }
  
  return t(`taskWorkspace.statusActions.${status}`)
}

// Methods
const loadPossibleStatuses = () => {
  try {
    const statuses = taskStateMachineService.getPossibleStatuses(props.currentStatus)
    possibleStatuses.value = statuses || []
  } catch (error) {
    console.error('Failed to load possible statuses:', error)
    notifications.notifyError(t('taskWorkspace.errors.loadFailed'))
    possibleStatuses.value = []
  }
}

const loadStatusTimeline = async () => {
  loadingTimeline.value = true
  
  try {
    const timeline = await projectTasksService.getTaskStatusTimeline(
      props.projectId.toString(), 
      props.taskId.toString()
    )
    statusTimeline.value = timeline || []
  } catch (error) {
    console.error('Failed to load task status timeline:', error)
    statusTimeline.value = []
  } finally {
    loadingTimeline.value = false
  }
}

const handleConfirm = async () => {
  if (!selectedStatus.value) return

  isLoading.value = true
  try {
    // Change task status using the new endpoint
    await projectTasksService.changeTaskStatus(
      props.projectId.toString(),
      props.taskId.toString(),
      selectedStatus.value,
      comment.value || undefined
    )
    
    notifications.notifySuccess(
      t('taskWorkspace.statusTransition.successMessage', { 
        status: t(`taskWorkspace.statusOptions.${selectedStatus.value}`) 
      })
    )
    
    // Reload timeline to show the new status
    await loadStatusTimeline()
    
    emit('success')
    emit('close')
    resetModal()
  } catch (error: any) {
    console.error('Failed to change task status:', error)
    
    // Check for specific error codes from backend
    const errorCode = error.response?.data?.code
    if (errorCode) {
      notifications.notifyError(t(`errors.business.${errorCode}`))
    } else {
      notifications.notifyError(t('taskWorkspace.statusTransition.errorMessage'))
    }
  } finally {
    isLoading.value = false
  }
}

const handleCancel = () => {
  if (!isLoading.value) {
    emit('cancel')
    emit('close')
    resetModal()
  }
}

const resetModal = () => {
  selectedStatus.value = null
  comment.value = ''
  possibleStatuses.value = []
  statusTimeline.value = []
}

// Timeline step icon generation
const getTimelineStepIcon = (status: string, step: TimelineStep): VNode => {
  const iconProps = {
    viewBox: '0 0 24 24',
    width: 20,
    height: 20
  }

  const commonProps = {
    fill: step.isCurrent || step.isCompleted ? 'currentColor' : 'none',
    stroke: 'currentColor',
    'stroke-width': 2
  }

  switch (status) {
    case 'PENDING':
      return h('svg', iconProps, [
        h('circle', { cx: 12, cy: 12, r: 8, ...commonProps })
      ])
    case 'IN_PROGRESS':
      return h('svg', iconProps, [
        h('path', { d: 'M8,5.14V19.14L19,12.14L8,5.14Z', fill: 'currentColor' })
      ])
    case 'ON_HOLD':
      return h('svg', iconProps, [
        h('rect', { x: 6, y: 4, width: 4, height: 16, rx: 1, fill: 'currentColor' }),
        h('rect', { x: 14, y: 4, width: 4, height: 16, rx: 1, fill: 'currentColor' })
      ])
    case 'COMPLETED':
      return h('svg', iconProps, [
        h('polyline', { 
          points: '20 6 9 17 4 12',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': 2 
        })
      ])
    case 'CANCELLED':
      return h('svg', iconProps, [
        h('line', { x1: 18, y1: 6, x2: 6, y2: 18, stroke: 'currentColor', 'stroke-width': 2 }),
        h('line', { x1: 6, y1: 6, x2: 18, y2: 18, stroke: 'currentColor', 'stroke-width': 2 })
      ])
    default:
      return h('svg', iconProps, [
        h('circle', { cx: 12, cy: 12, r: 8, ...commonProps })
      ])
  }
}

// Watch for modal visibility changes
watch(() => props.isVisible, (visible: boolean) => {
  if (visible) {
    loadPossibleStatuses()
    loadStatusTimeline()
  } else {
    resetModal()
  }
}, { immediate: true })
</script>

<style scoped>
.task-status-transition-modal {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0.5rem;
}

/* Timeline Section */
.task-status-transition-modal__timeline {
  padding: 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 1rem;
  border: 1px solid var(--border-color, #e5e7eb);
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
  position: relative;
  overflow: hidden;
}

.task-status-transition-modal__timeline::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary, #3b82f6), var(--primary-600, #2563eb));
}

.timeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.75rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.timeline-section-title {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--text-primary, #111827);
  margin: 0;
  letter-spacing: -0.01em;
}

.timeline-section-icon {
  color: var(--primary, #3b82f6);
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.2));
}

.timeline-current-status {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.timeline-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--text-secondary, #6b7280);
  font-size: 0.875rem;
  font-weight: 500;
}

.integrated-timeline {
  display: flex;
  align-items: flex-start;
  gap: 0;
  overflow-x: auto;
  padding: 1.5rem 0;
  -webkit-overflow-scrolling: touch;
}

.integrated-timeline::-webkit-scrollbar {
  height: 6px;
}

.integrated-timeline::-webkit-scrollbar-track {
  background: var(--surface-secondary, #f1f5f9);
  border-radius: 3px;
}

.integrated-timeline::-webkit-scrollbar-thumb {
  background: var(--primary-300, #93c5fd);
  border-radius: 3px;
}

.integrated-timeline::-webkit-scrollbar-thumb:hover {
  background: var(--primary-400, #60a5fa);
}

.timeline-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  min-width: 140px;
  width: 140px;
  flex-shrink: 0;
  position: relative;
}

.timeline-step::after {
  content: '';
  position: absolute;
  top: 24px;
  left: 60%;
  width: 100%;
  height: 3px;
  background: var(--border-color, #e5e7eb);
  z-index: 0;
  border-radius: 3px;
}

.timeline-step:last-child::after {
  display: none;
}

.timeline-step.current::after {
  background: linear-gradient(to right, var(--primary, #3b82f6) 0%, var(--border-color, #e5e7eb) 100%);
}

.timeline-step.completed::after {
  background: var(--success, #10b981);
}

.timeline-step-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid var(--border-color, #e5e7eb);
  background: var(--surface, #ffffff);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  color: var(--text-muted, #9ca3af);
}

.timeline-step.current .timeline-step-icon {
  border-color: var(--primary, #3b82f6);
  background: linear-gradient(135deg, var(--primary, #3b82f6), var(--primary-600, #2563eb));
  color: white;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15), 0 4px 12px rgba(59, 130, 246, 0.3);
  transform: scale(1.15);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.15), 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.1), 0 4px 16px rgba(59, 130, 246, 0.4);
  }
}

.timeline-step.completed .timeline-step-icon {
  border-color: var(--success, #10b981);
  background: linear-gradient(135deg, var(--success, #10b981), var(--success-600, #059669));
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.timeline-step-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
}

.timeline-step-label {
  font-size: 0.8125rem;
  font-weight: 700;
  text-align: center;
  color: var(--text-secondary, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
  letter-spacing: -0.01em;
}

.timeline-step.current .timeline-step-label {
  color: var(--primary, #3b82f6);
  font-weight: 800;
}

.timeline-step.completed .timeline-step-label {
  color: var(--success-700, #047857);
  font-weight: 700;
}

.timeline-step-date {
  font-size: 0.6875rem;
  color: var(--text-muted, #9ca3af);
  text-align: center;
  white-space: nowrap;
  font-family: 'Courier New', monospace;
  padding: 0.125rem 0.5rem;
  background: var(--surface, #ffffff);
  border-radius: 0.25rem;
  border: 1px solid var(--border-color, #e5e7eb);
}

.timeline-step.current .timeline-step-date {
  color: var(--primary-700, #1d4ed8);
  font-weight: 600;
  background: var(--primary-50, #eff6ff);
  border-color: var(--primary-200, #bfdbfe);
}

.task-status-transition-modal__label {
  display: block;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--text-color-primary, #111827);
  margin-bottom: 1rem;
  letter-spacing: -0.01em;
}

.current-status-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-color-secondary, #6b7280);
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  transition: all 0.2s ease;
}

.status-badge--pending {
  background: linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%);
  color: var(--neutral-700, #404040);
}

.status-badge--in_progress {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: var(--info-700, #1e40af);
}

.status-badge--on_hold {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: var(--warning-700, #b45309);
}

.status-badge--completed {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: var(--success-700, #047857);
}

.status-badge--cancelled {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: var(--error-700, #b91c1c);
}

/* Status Options Grid */
.status-options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.875rem;
}

.status-option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border: 2px solid var(--border-color, #e5e7eb);
  border-radius: 0.625rem;
  background: var(--surface-primary, #ffffff);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.status-option:hover {
  border-color: var(--primary-400, #60a5fa);
  background: linear-gradient(135deg, #ffffff 0%, #eff6ff 100%);
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgb(59 130 246 / 0.15), 0 4px 6px -4px rgb(59 130 246 / 0.1);
}

.status-option--selected {
  border-color: var(--primary-500, #3b82f6);
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  box-shadow: 0 10px 15px -3px rgb(59 130 246 / 0.2), 0 4px 6px -4px rgb(59 130 246 / 0.15);
  transform: translateY(-2px);
}

.status-option--selected::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary, #3b82f6), var(--primary-600, #2563eb));
  border-radius: 0.875rem 0.875rem 0 0;
}

.status-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  flex-shrink: 0;
  transition: all 0.25s ease;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
}

.status-option:hover .status-option__icon,
.status-option--selected .status-option__icon {
  transform: scale(1.1);
}

.status-option__icon--pending {
  background: linear-gradient(135deg, #f5f5f5 0%, #e5e5e5 100%);
  color: var(--neutral-600, #525252);
}

.status-option__icon svg {
  width: 18px;
  height: 18px;
}

.status-option__icon--in_progress {
  background: linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%);
  color: var(--info-600, #2563eb);
}

.status-option__icon--on_hold {
  background: linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%);
  color: var(--warning-600, #d97706);
}

.status-option__icon--completed {
  background: linear-gradient(135deg, #d1fae5 0%, #6ee7b7 100%);
  color: var(--success-600, #059669);
}

.status-option__icon--cancelled {
  background: linear-gradient(135deg, #fee2e2 0%, #fca5a5 100%);
  color: var(--error-600, #dc2626);
}

.status-option__content {
  flex: 1;
  min-width: 0;
}

.status-option__title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-color-primary, #111827);
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.status-option__check {
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: linear-gradient(135deg, var(--primary-500, #3b82f6), var(--primary-600, #2563eb));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 4px -1px rgb(59 130 246 / 0.3);
  animation: checkIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.status-option__check svg {
  width: 14px;
  height: 14px;
}

@keyframes checkIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Comment Field */
.task-status-transition-modal__comment {
  margin-top: 0.5rem;
}

.comment-textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--border-color, #e5e7eb);
  border-radius: 0.875rem;
  font-size: 0.9375rem;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  transition: all 0.2s ease;
  background: var(--surface-primary, #ffffff);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.comment-textarea:hover {
  border-color: var(--primary-300, #93c5fd);
}

.comment-textarea:focus {
  outline: none;
  border-color: var(--primary-500, #3b82f6);
  box-shadow: 0 0 0 4px var(--primary-100, #dbeafe), 0 1px 3px 0 rgb(0 0 0 / 0.1);
  background: #ffffff;
}

.comment-textarea::placeholder {
  color: var(--text-color-tertiary, #9ca3af);
  font-style: italic;
}

/* Empty State */
.task-status-transition-modal__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  background: var(--surface-secondary, #f9fafb);
  border-radius: 0.875rem;
  border: 2px dashed var(--border-color, #e5e7eb);
}

.task-status-transition-modal__empty-icon {
  color: var(--text-color-tertiary, #9ca3af);
  margin-bottom: 1rem;
  opacity: 0.5;
}

.task-status-transition-modal__empty-text {
  font-size: 0.9375rem;
  color: var(--text-color-secondary, #6b7280);
  margin: 0;
  font-weight: 500;
}

/* Actions */
.task-status-transition-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.875rem;
  padding-top: 0.5rem;
}

/* Responsive */
  @media (max-width: 768px) {
  .task-status-transition-modal {
    padding: 0;
  }

  .task-status-transition-modal__timeline {
    padding: 1.5rem;
  }

  .timeline-step {
    min-width: 110px;
    width: 110px;
  }

  .timeline-step-label {
    font-size: 0.75rem;
    max-width: 100px;
  }

  .timeline-step-icon {
    width: 40px;
    height: 40px;
  }

  .timeline-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .timeline-current-status {
    width: 100%;
  }

  .current-status-label {
    font-size: 0.75rem;
  }

  .status-options-grid {
    grid-template-columns: 1fr;
  }

  .task-status-transition-modal__actions {
    flex-direction: column-reverse;
  }

  .task-status-transition-modal__actions button {
    width: 100%;
  }
}/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .task-status-transition-modal__timeline {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  }

  .status-option {
    background: #1e293b;
  }

  .status-option:hover,
  .status-option--selected {
    background: linear-gradient(135deg, #1e293b 0%, #1e3a5f 100%);
  }

  .comment-textarea {
    background: #1e293b;
    color: #f1f5f9;
  }

  .comment-textarea:focus {
    background: #0f172a;
  }
}
</style>
